import React from "react";
import { Button, SecondaryButton } from "components/Form";
import { useDispatch, useSelector } from "react-redux";
import { Form, Formik } from "formik";
import {
  getAgencyByDeploymentId,
  getConsignmentOptionsByDeploymentSaleId,
  getCurrentDeploymentSalesList,
  getDeploymentSaleIdByConsignmentId,
  getDeploymentSaleIdByLivestockSaleIdAndAgencyId,
  getEnvdById,
  getNameByDeploymentId,
} from "selectors";
import { SelectField } from "components/Form/FormikControls";
import { consignmentOptionFilter } from "lib/filters";
import { DeploymentSelectField } from "components/Form/Fields";

import { useFieldState, useFieldValue } from "hooks";
import { getLivestockSaleId } from "lib/navigation";
import { EnvdDocumentAction } from "actions";

import { ForSaleyardAdmin } from "containers/ForUserType";
import { getSuggestedConsignmentIdByCandidateDeclarationId } from "selectors/candidateDeclarations";
import { Row } from "components/Layout";

interface ConsignmentPickerProps {
  deploymentSaleId: number | null;
}

function ConsignmentPicker(props: ConsignmentPickerProps) {
  const { deploymentSaleId } = props;
  const consignmentOptions = useSelector(
    getConsignmentOptionsByDeploymentSaleId(deploymentSaleId),
  );

  return (
    <SelectField
      name="consignmentId"
      options={consignmentOptions}
      label="Owner / Vendor"
      filterOption={consignmentOptionFilter}
      suggestedValueFieldName={"suggestedConsignmentId"}
    />
  );
}

interface FormFieldsProps {
  eNvdId: string;
  getNextAttachment: () => void;
}

function FormFields(props: FormFieldsProps) {
  const { eNvdId, getNextAttachment } = props;

  const deploymentId = useFieldValue("deploymentId");
  const [consignmentId, setConsignmentId] = useFieldState("consignmentId");
  const agencyId = useSelector(getAgencyByDeploymentId(deploymentId))?.id;

  const deploymentSaleId = useSelector(
    getDeploymentSaleIdByLivestockSaleIdAndAgencyId(
      getLivestockSaleId(),
      agencyId,
    ),
  );

  function clearConsignmentOnDeploymentChange() {
    if (consignmentId) {
      setConsignmentId(null);
    }
  }

  return (
    <>
      <ForSaleyardAdmin>
        <DeploymentSelectField
          label="Agency"
          name="deploymentId"
          required
          onChangeExtra={clearConsignmentOnDeploymentChange}
          retrictToSaleDeployments
          restrictToOwnDeployments
          suggestedValueFieldName={"suggestedDeploymentId"}
        />
      </ForSaleyardAdmin>
      <ConsignmentPicker deploymentSaleId={deploymentSaleId} />
      <Row paddingVertical={2}>
        <FormDismissAction
          eNvdId={eNvdId}
          getNextAttachment={getNextAttachment}
        />
        <FormAllocateAction
          deploymentId={deploymentId}
          consignmentId={consignmentId}
        />
      </Row>
    </>
  );
}

interface FormDismissActionProps {
  eNvdId: string;
  getNextAttachment: () => void;
}
function FormDismissAction(props: FormDismissActionProps) {
  const { eNvdId, getNextAttachment } = props;

  const dispatch = useDispatch();
  const eNvd = useSelector(getEnvdById(eNvdId));

  const onClickDismiss = () => {
    const newExclusions = [...eNvd.excludedFromLivestockSaleIds];
    newExclusions.push(getLivestockSaleId());
    dispatch(
      EnvdDocumentAction.update(
        {
          id: eNvdId,
          excludedFromLivestockSaleIds: newExclusions,
          livestockSaleId: null,
        },
        { changeReason: "Dismissed from Sale from Unused eNvd form" },
      ),
    );
    getNextAttachment();
  };

  return (
    <SecondaryButton type="button" onClick={onClickDismiss}>
      Dismiss from this Sale
    </SecondaryButton>
  );
}

interface FormAllocateActionsProps {
  deploymentId: number | null;
  consignmentId: string | null;
}

function FormAllocateAction(props: FormAllocateActionsProps) {
  const { deploymentId, consignmentId } = props;

  const agencyName = useSelector(getNameByDeploymentId(deploymentId));

  let submitAction = null;

  if (deploymentId && consignmentId) {
    submitAction = "Assign to Vendor";
  } else if (deploymentId && !consignmentId) {
    submitAction = `Assign to ${agencyName}`;
  }

  const isSubmitDisabled = submitAction === null;
  return (
    <Button type="submit" disabled={isSubmitDisabled}>
      {submitAction || "Assign to Sale"}
    </Button>
  );
}

interface FormValues {
  deploymentId: number | null;
  consignmentId: string | null;
  suggestedConsignmentId: string | null;
  suggestedDeploymentId: number | null;
}

interface UnusedEnvdFormProps {
  candidateDeclarationId: string;
  eNvdId: string;
  getNextAttachment: () => void;
}

function UnusedEnvdForm(props: UnusedEnvdFormProps) {
  const { candidateDeclarationId, eNvdId, getNextAttachment } = props;
  const dispatch = useDispatch();
  const eNvd = useSelector(getEnvdById(eNvdId));

  const suggestedConsignmentId: string | null = useSelector(
    getSuggestedConsignmentIdByCandidateDeclarationId(candidateDeclarationId),
  );

  const suggestedDeploymentSaleId = useSelector(
    getDeploymentSaleIdByConsignmentId(suggestedConsignmentId),
  );
  const suggestedDeploymentId =
    useSelector(
      state =>
        getCurrentDeploymentSalesList(state)?.find(
          ds => ds.deployment_sale_id === suggestedDeploymentSaleId,
        )?.deployment_id,
    ) || null;

  const onSubmit = (values: FormValues) => {
    if (values.deploymentId && values.consignmentId) {
      dispatch(
        EnvdDocumentAction.createConsignmentAttachments({
          id: eNvdId,
          consignmentId: values.consignmentId,
        }),
      );
    } else if (values.deploymentId && !values.consignmentId) {
      dispatch(
        EnvdDocumentAction.update(
          { id: eNvdId, deploymentId: values.deploymentId }, // Paylod to just update the Deployment - the backend will handle updateing the CandidateDeclaration
          { changeReason: "Deployment changes from Unused eNvds form" },
        ),
      );
    }
    getNextAttachment();
  };

  const initialValues: FormValues = {
    deploymentId: eNvd.deploymentId,
    consignmentId: null,
    suggestedConsignmentId,
    suggestedDeploymentId,
  };

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit}>
      <Form>
        <FormFields eNvdId={eNvdId} getNextAttachment={getNextAttachment} />
      </Form>
    </Formik>
  );
}

export default UnusedEnvdForm;
